// utils/marketConfig.js
// Market-aware cost configuration and shared payout math.
// Market is the unit of configuration — not driver, not device.
// Utah (Hill AFB) is Market 1. Formula shape is platform-wide.
// Original IP — LoadDash Services LLC

import { COMPANY_MARGIN_PCT, STRIPE_FEE_PCT, QUICK_SURCHARGE, QUICK_COMPANY_CUT, MATTRESS_SURCHARGE } from "../constants";

// ── Market Definitions ──────────────────────────────────────
// Each market defines the variable cost inputs that differ by geography.
// Platform-wide constants (COMPANY_MARGIN_PCT, STRIPE_FEE_PCT, LEU chain,
// tier pricing, capacity logic) stay in constants.js.

export const MARKETS = {
  "ut-hill-afb": {
    id: "ut-hill-afb",
    label: "Hill AFB / Wasatch Front",
    timezone: "America/Denver",
    service_days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    dump_fee: 10.00,
    fuel_rate_per_mile: 0.67,
    tier_prices: { Small: 75, Half: 120, ThreeQ: 175, Full: 295 },
    zones: {
      A: { avg_miles: 4.2 },
      B: { avg_miles: 3.1 },
      C: { avg_miles: 5.8 },
      D: { avg_miles: 6.4 },
    },
  },
  "tx-fort-cavazos": {
    id: "tx-fort-cavazos",
    label: "Fort Cavazos / Killeen TX",
    timezone: "America/Chicago",
    service_days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    dump_fee: 6.88,
    fuel_rate_per_mile: 0.67,
    tier_prices: { Small: 75, Half: 120, ThreeQ: 175, Full: 295 },
    zones: {
      A: { avg_miles: 3.2 },
      B: { avg_miles: 4.8 },
      C: { avg_miles: 6.1 },
      D: { avg_miles: 7.4 },
    },
  },
};

/** Get a market config by ID.
 * Multi-market mode: unknown or missing market IDs must throw.
 * Silent fallback is not allowed because it can misprice jobs.
 */
export function getMarket(marketId) {
  if (!marketId) {
    throw new Error("Missing marketId in getMarket()");
  }

  const market = MARKETS[marketId];

  if (!market) {
    throw new Error(`Unknown marketId: ${marketId}`);
  }

  return market;
}

/** Get avg miles for a zone within a market. */
export function getZoneMiles(marketId, zoneKey) {
  const m = getMarket(marketId);
  return m.zones[zoneKey]?.avg_miles ?? null;
}

/** Get tier price for a market. Falls back to market's tier_prices table. */
export function getTierPrice(marketId, tierKey) {
  const m = getMarket(marketId);
  return m.tier_prices?.[tierKey] ?? null;
}

// ── Viability ───────────────────────────────────────────────
// Minimum driver profit after all costs. Platform-wide constant.
// A tier/zone combination is non-viable if driverPay < this threshold.
export const MIN_DRIVER_PROFIT_BUFFER = 20;

/**
 * Minimum gross price needed for a tier to be viable in a market/zone.
 * Solves: gross - stripeFee - dumpFee - fuelEst - companyMargin >= MIN_DRIVER_PROFIT_BUFFER
 * Which is: gross - gross*STRIPE_FEE_PCT - dumpFee - fuelEst - gross*COMPANY_MARGIN_PCT >= buffer
 *           gross * (1 - STRIPE_FEE_PCT - COMPANY_MARGIN_PCT) >= buffer + dumpFee + fuelEst
 */
export function minimumViablePrice(marketId, zoneKey) {
  const market = getMarket(marketId);
  const miles = zoneKey ? (market.zones[zoneKey]?.avg_miles ?? 0) : 0;
  const fuelEst = Math.round(miles * market.fuel_rate_per_mile * 100) / 100;
  const fixedCosts = market.dump_fee + fuelEst;
  const marginRate = 1 - STRIPE_FEE_PCT - COMPANY_MARGIN_PCT;
  return Math.ceil(((MIN_DRIVER_PROFIT_BUFFER + fixedCosts) / marginRate) * 100) / 100;
}

/** Check if a tier is economically viable for a market/zone combination.
 * Viability means estimated PROFIT after the Hauler's own dump and fuel,
 * not the transfer amount — numerically identical to the pre-ruling gate. */
export function isTierViable(marketId, zoneKey, tierKey) {
  const price = getTierPrice(marketId, tierKey);
  if (price == null) return false;
  const { estProfit } = calcSplit(price, marketId, zoneKey);
  return estProfit >= MIN_DRIVER_PROFIT_BUFFER;
}

// ── Shared Payout Math ──────────────────────────────────────
// Owner ruling 2026-07-10: LoadDash takes ONLY the card fee, the 10% platform
// fee, and $5 of a Quick rush fee. Dump and fuel are the Hauler's own
// out-of-pocket business costs. They appear on the courtesy receipt as
// estimates (fuelEst / dumpFee / estProfit) and are NEVER withheld from the
// transfer. Formula shape (platform-wide):
//   TRANSFER (driverPay) = CHARGED_GROSS - STRIPE_FEE - COMPANY_MARGIN
//   EST_PROFIT           = TRANSFER - DUMP_EST - FUEL_EST - MATTRESS_PASSTHROUGH
//
// Inputs that vary by market: dump_fee, fuel_rate_per_mile, zone avg_miles.
// Inputs that stay global: STRIPE_FEE_PCT, COMPANY_MARGIN_PCT, tier prices.

/**
 * Driver's share of the Quick surcharge (owner ruling 2026-07-07):
 * surcharge minus the flat company cut minus Stripe's fee on the surcharge.
 * Fixed (~$13.33) regardless of tier/zone, so the driver sees one clean number.
 */
export const QUICK_DRIVER_BONUS = Math.round(
  (QUICK_SURCHARGE - QUICK_COMPANY_CUT - Math.round(QUICK_SURCHARGE * STRIPE_FEE_PCT * 100) / 100) * 100
) / 100;

/**
 * Compute Model A earnings split using market-aware cost inputs.
 * @param {number} gross - tier price charged to customer
 * @param {string} marketId - market identifier
 * @param {string|null} zoneKey - zone key (A/B/C/D) for mileage lookup
 * @param {{ quick?: boolean }} [options] - quick=true adds the Quick Pickup
 *   surcharge split (company +QUICK_COMPANY_CUT, Stripe on the surcharge,
 *   driver +QUICK_DRIVER_BONUS). Defaults to a non-quick job.
 * @returns {{ gross, stripeFee, fuelEst, dumpFee, variableCosts, companyMargin, driverPay, quick }}
 */
/**
 * Normalize a mattress piece count: NULL/undefined/garbage/negative → 0,
 * fractions truncate. Shared by calcSplit and buildCostSnapshot so the two
 * can never disagree on what counts as a piece.
 */
const normalizeMattressPieces = (n) => Math.max(0, Math.trunc(Number(n) || 0));

export function calcSplit(gross, marketId, zoneKey, { quick = false, mattressPieces } = {}) {
  const market = getMarket(marketId);
  const miles = zoneKey ? (market.zones[zoneKey]?.avg_miles ?? null) : null;

  // Quick Pickup surcharge split. The surcharge is added to the charged gross;
  // the company keeps a flat cut, Stripe takes its fee on the surcharge, and the
  // remainder falls through to the driver. Non-quick jobs: all terms are 0.
  const quickSurcharge = quick ? QUICK_SURCHARGE : 0;
  const quickCompanyCut = quick ? QUICK_COMPANY_CUT : 0;
  const quickStripeFee = quick ? Math.round(QUICK_SURCHARGE * STRIPE_FEE_PCT * 100) / 100 : 0;

  // Mattress disposal reimbursement (owner ruling 2026-07-10). Wasatch Integrated
  // bills $15/piece at the dump scale and the Hauler pays it, so the customer
  // surcharge is reimbursed to the Hauler IN FULL — they come out whole on the
  // gate fee. The company absorbs Stripe's fee on the surcharge: it is added to
  // stripeFee and netted back out of companyMargin, so the reimbursement reaching
  // the Hauler is the full MATTRESS_SURCHARGE per piece, not shaved by Stripe.
  // mattressPieces normalizes to 0 when absent → every term below is 0 →
  // byte-identical to a non-mattress job (characterization-locked).
  const pieces = normalizeMattressPieces(mattressPieces);
  const mattressSurcharge = pieces * MATTRESS_SURCHARGE;
  const mattressStripeFee = Math.round(mattressSurcharge * STRIPE_FEE_PCT * 100) / 100;

  const stripeFee = Math.round(gross * STRIPE_FEE_PCT * 100) / 100 + quickStripeFee + mattressStripeFee;
  const fuelEst = miles != null ? Math.round(miles * market.fuel_rate_per_mile * 100) / 100 : 0;
  // Courtesy-receipt aggregate only (legacy variable_cost_amt persistence).
  // These costs belong to the Hauler and are not withheld from the transfer.
  const variableCosts = Math.round((stripeFee + market.dump_fee + fuelEst) * 100) / 100;
  const companyMargin = Math.round(gross * COMPANY_MARGIN_PCT * 100) / 100 + quickCompanyCut - mattressStripeFee;
  const chargedGross = gross + quickSurcharge + mattressSurcharge;
  // The transfer (owner ruling 2026-07-10): everything the customer paid minus
  // the processor's cut and LoadDash's platform take. Dump and fuel are the
  // Hauler's own out-of-pocket costs and are NOT withheld.
  const driverPay = Math.round((chargedGross - stripeFee - companyMargin) * 100) / 100;
  // Estimated profit after the Hauler's own costs, for the courtesy receipt
  // and viability. The mattress reimbursement passes through 1:1 to the dump
  // gate, so it nets out of profit. For a non-mattress job this equals the
  // pre-ruling driverPay number (e.g. the guide's $89.51 Zone D Medium).
  const estProfit = Math.round((driverPay - market.dump_fee - fuelEst - mattressSurcharge) * 100) / 100;

  return { gross: chargedGross, stripeFee, fuelEst, dumpFee: market.dump_fee, variableCosts, companyMargin, driverPay, estProfit, mattressReimbursement: mattressSurcharge };
}

/** The only live market. Used as the default when a job row is missing market_id. */
export const DEFAULT_MARKET_ID = "ut-hill-afb";

/**
 * calcSplit that never throws. For render-path aggregates (driver job lists,
 * route stops) where a single row with a missing/unknown market_id must not
 * blank the entire screen:
 *   - null/empty marketId  → defaults to DEFAULT_MARKET_ID (consistent with
 *     useJobEconomics / haulerPayDisclosure; the only live market is Utah, so a
 *     missing market_id is a not-yet-backfilled Utah job, not a real reprice).
 *   - unknown marketId     → returns a zeroed split (contributes nothing to the
 *     aggregate) instead of throwing.
 * The persist/completion path keeps using calcSplit() directly so a genuinely
 * unknown market there still surfaces rather than silently mispricing a payout.
 */
export function safeCalcSplit(gross, marketId, zoneKey, options = {}) {
  try {
    return calcSplit(gross, marketId || DEFAULT_MARKET_ID, zoneKey, options);
  } catch {
    return { gross: gross || 0, stripeFee: 0, fuelEst: 0, dumpFee: 0, variableCosts: 0, companyMargin: 0, driverPay: 0, estProfit: 0, mattressReimbursement: 0 };
  }
}

/**
 * Build a cost snapshot for persistence at completion time.
 * Freezes the exact assumptions used so future config changes don't rewrite history.
 */
export function buildCostSnapshot(marketId, zoneKey, { quick = false, mattressPieces } = {}) {
  const market = getMarket(marketId);
  const avgMiles = zoneKey ? (market.zones[zoneKey]?.avg_miles ?? null) : null;
  const pieces = normalizeMattressPieces(mattressPieces);
  return {
    market_id: market.id,
    zone: zoneKey || null,
    avg_miles: avgMiles,
    dump_fee: market.dump_fee,
    fuel_rate_per_mile: market.fuel_rate_per_mile,
    company_margin_pct: COMPANY_MARGIN_PCT,
    stripe_fee_pct: STRIPE_FEE_PCT,
    quick,
    quick_surcharge: quick ? QUICK_SURCHARGE : 0,
    quick_company_cut: quick ? QUICK_COMPANY_CUT : 0,
    // Mattress disposal reimbursement frozen at completion. Full surcharge is
    // reimbursed to the Hauler; the company absorbs Stripe's fee on it.
    mattress_pieces: pieces,
    mattress_surcharge: pieces * MATTRESS_SURCHARGE,
    mattress_reimbursement: pieces * MATTRESS_SURCHARGE,
  };
}
